import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ledgerApi } from "../lib/ledgerApi";
import HashString from "../components/HashString";
import ProofPanel from "../components/ProofPanel";
import FederationBadge from "../components/FederationBadge";
import { ArrowLeft, Bot, Copy } from "lucide-react";
import { toast } from "sonner";

const KV = ({ label, children, mono }) => (
  <div className="grid grid-cols-12 gap-3 py-3 border-b border-border last:border-0">
    <div className="col-span-12 sm:col-span-3 text-[10px] sm:text-xs uppercase tracking-[0.2em] text-muted-foreground self-center">{label}</div>
    <div className={`col-span-12 sm:col-span-9 ${mono ? "font-mono" : ""} text-sm break-all`}>{children}</div>
  </div>
);

export default function TransactionDetail() {
  const { id } = useParams();
  const [tx, setTx] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    setTx(null);
    setError(null);
    ledgerApi
      .getTx(id)
      .then((t) => alive && setTx(t))
      .catch((e) => alive && setError(e?.response?.data?.detail || e.message));
    const i = setInterval(() => {
      ledgerApi.getTx(id).then((t) => alive && setTx(t)).catch(() => {});
    }, 3000);
    return () => {
      alive = false;
      clearInterval(i);
    };
  }, [id]);

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-8 py-10 md:py-14">
      <Link to="/blocks" className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground mb-6" data-testid="back-blocks-from-tx">
        <ArrowLeft className="w-3 h-3" strokeWidth={1.5} /> Back
      </Link>
      <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground mb-2">/ Transaction</div>
      <h1 className="text-2xl sm:text-3xl tracking-tight font-medium mb-8 break-all font-mono" data-testid="tx-detail-id">{id}</h1>

      {error && <div className="border border-status-tampered/40 bg-status-tampered/10 p-4 rounded-sm text-status-tampered text-sm">{error}</div>}
      {!tx && !error && <div className="text-sm text-muted-foreground">Loading…</div>}

      {tx && (
        <div className="border border-border rounded-sm bg-card p-4 sm:p-6">
          <KV label="Status" mono>
            <span
              className={`px-2 py-0.5 border rounded-sm text-xs uppercase tracking-widest ${
                tx.status === "confirmed"
                  ? "border-status-ok/40 bg-status-ok/10 text-status-ok"
                  : "border-status-info/40 bg-status-info/10 text-status-info"
              }`}
              data-testid="tx-status"
            >
              {tx.status}
            </span>
            {tx.block_number != null && (
              <Link to={`/blocks/${tx.block_number}`} className="ml-3 text-xs text-muted-foreground hover:underline">
                in block #{tx.block_number}
              </Link>
            )}
            {tx.block_number != null && (
              <span className="ml-3 inline-block">
                <FederationBadge blockNumber={tx.block_number} testid="tx-federation-badge" size="sm" />
              </span>
            )}
          </KV>
          <KV label="Kind" mono>
            {tx.kind}
            {tx.bundle_role && <span className="ml-2 px-1.5 py-0.5 border border-status-info/40 bg-status-info/10 text-status-info text-[10px] uppercase tracking-widest rounded-sm">{tx.bundle_role}</span>}
            {tx.agent_did && (
              <span
                data-testid="tx-agent-badge"
                className="ml-2 inline-flex items-center gap-1 px-1.5 py-0.5 border border-foreground/30 bg-foreground/5 text-[10px] uppercase tracking-widest rounded-sm"
              >
                <Bot className="w-2.5 h-2.5" strokeWidth={1.5} /> Agent
              </span>
            )}
          </KV>
          {tx.agent_did && (
            <KV label="Agent" mono>
              <button
                data-testid="tx-agent-did"
                onClick={() => { navigator.clipboard.writeText(tx.agent_did); toast.success("DID copied"); }}
                className="inline-flex items-center gap-1.5 hover:underline"
                title={tx.agent_did}
              >
                <span className="break-all">{tx.agent_did}</span>
                <Copy className="w-3 h-3 shrink-0" strokeWidth={1.5} />
              </button>
              <div className="text-[10px] text-muted-foreground mt-0.5 normal-case">
                Public DID only · policy &amp; keys are not exposed
              </div>
            </KV>
          )}
          {tx.bundle_id && (
            <KV label="Bundle" mono>
              <HashString value={tx.bundle_id} label="Bundle id" testid="tx-bundle-id" />
              <span className="ml-2 text-[11px] text-muted-foreground">atomic 4-leg cross-currency settlement</span>
            </KV>
          )}
          <KV label="From" mono>
            {tx.kind === "coinbase" ? (
              <span className="text-muted-foreground">{tx.from_address} <span className="ml-2 text-[10px] uppercase tracking-[0.2em]">coinbase</span></span>
            ) : (
              <Link to={`/wallets/${tx.from_address}`} className="hover:underline">{tx.from_address}</Link>
            )}
          </KV>
          <KV label="To" mono>
            <Link to={`/wallets/${tx.to_address}`} className="hover:underline">{tx.to_address}</Link>
          </KV>
          <KV label="Amount" mono>{tx.amount} <span className="text-muted-foreground">{tx.currency}</span></KV>
          <KV label="Nonce" mono>{tx.nonce}</KV>
          <KV label="Timestamp" mono>{new Date(tx.timestamp).toLocaleString()}</KV>
          <KV label="Pubkey" mono>
            <HashString value={tx.pubkey} label="Pubkey" />
          </KV>
          <KV label="Signature" mono>
            <HashString value={tx.signature} label="Signature" />
          </KV>
          <KV label="Tx id" mono>
            <HashString value={tx.tx_id} label="Tx id" />
          </KV>
        </div>
      )}
      {tx && tx.status === "confirmed" && (
        <div className="mt-6">
          <ProofPanel txId={id} />
        </div>
      )}
    </div>
  );
}
